/**
 * Inbox alert helpers: sound + in-app toast + browser notifications.
 *
 * The in-app (Sonner) toast is the guaranteed layer: it does not need OS
 * Notification permission and fires whenever the agent is signed in, on any
 * dashboard page. Desktop Notification is extra for a hidden/minimized window.
 *
 * Socket and poll inputs both enter through InboxAlertDispatcher so ordering,
 * identity normalization, and duplicate handling are consistent.
 */

import { toast } from "sonner";
import {
  createInboxAlertFingerprint,
  FALLBACK_DEDUPE_MS,
  shouldAlertOnce,
} from "@/lib/inboxAlertDedupe";
import { getInboxSelectedConversationId } from "@/lib/inboxAlertSelection";
import { InboxMessageSoundPolicy } from "@/lib/InboxMessageSoundPolicy";
import { InboxAlertAudioController } from "@/lib/InboxAlertAudioController";
import { InboxAlertDispatcher } from "@/lib/InboxAlertDispatcher";

const inboxMessageSoundPolicy = new InboxMessageSoundPolicy();

const audioController = new InboxAlertAudioController({
  onBlocked: (unlockAndTest) => {
    toast.warning("Inbox sound needs permission", {
      id: "inbox-audio-unlock",
      description: "Click to enable and test inbox alert sound.",
      duration: 15_000,
      action: {
        label: "Enable sound",
        onClick: () => void unlockAndTest(),
      },
    });
  },
});

/** Play the inbox chirp now; blocked sounds are dropped, never queued. */
export function playInboxMessageSound() {
  return audioController.play();
}

// ---------------------------------------------------------------------------
// Preferences (workspace notification settings, refreshed by the alerts hook)
// ---------------------------------------------------------------------------

const prefs = { desktopEnabled: true, soundEnabled: true, inAppEnabled: true };

/** @param {{ desktopEnabled?: boolean, soundEnabled?: boolean, inAppEnabled?: boolean }} next */
export function setInboxAlertPrefs(next = {}) {
  if (typeof next.desktopEnabled === "boolean") prefs.desktopEnabled = next.desktopEnabled;
  if (typeof next.soundEnabled === "boolean") prefs.soundEnabled = next.soundEnabled;
  if (typeof next.inAppEnabled === "boolean") prefs.inAppEnabled = next.inAppEnabled;
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

let _notifPermissionInFlight = false;

/** Try to request notification permission. Safe to call any time. */
export async function requestInboxNotificationPermission() {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "default") return;
  if (_notifPermissionInFlight) return;
  _notifPermissionInFlight = true;
  try {
    await Notification.requestPermission();
  } catch {
    /* retry on the next gesture */
  } finally {
    _notifPermissionInFlight = false;
  }
}

/** Show a desktop notification, requesting permission first if needed. */
export async function showInboxMessageNotification({ title, body, sessionId } = {}) {
  if (typeof Notification === "undefined") return;
  if (Notification.permission === "default") {
    await requestInboxNotificationPermission();
  }
  if (Notification.permission !== "granted") return;

  try {
    const n = new Notification(title || "New inbox message", {
      body: body || "A customer sent a message.",
      tag: sessionId ? `inbox-${sessionId}` : "inbox-message",
      icon: "/logo192.png",
      silent: true, // custom sound plays above; suppress OS duplicate
    });
    n.onclick = () => {
      window.focus();
      window.location.assign(sessionId ? `/chat?session=${encodeURIComponent(sessionId)}` : "/chat");
      n.close();
    };
  } catch { /* ignore */ }
}

/**
 * In-app toast. Always shown (no OS permission). This is the layer that must
 * fire while the dashboard is open, including when desktop notifications are
 * blocked or the agent is on another page.
 */
export function showInboxAppNotification({ title, body, sessionId, tone = "info" } = {}) {
  const href = sessionId ? `/chat?session=${encodeURIComponent(sessionId)}` : "/chat";
  const show = tone === "warning" ? toast.warning : toast.info;
  show(title || "New inbox message", {
    id: sessionId ? `inbox-app-${sessionId}` : "inbox-app",
    description: body || "A customer sent a message.",
    duration: 12_000,
    action: {
      label: "Open",
      onClick: () => {
        window.location.assign(href);
      },
    },
  });
}

// ---------------------------------------------------------------------------
// Bootstrap — call once on app mount from useInboxMessageAlerts
// ---------------------------------------------------------------------------

let _bootstrapped = false;

/**
 * Arm the audio context and request notification permission on a real gesture.
 *
 * Browsers ignore or auto-deny Notification.requestPermission() without a
 * click/key, so the prompt is tied to the same first interaction that unlocks
 * AudioContext. Resume is idempotent so later alerts still play in a hidden tab.
 */
export function bootstrapInboxAlerts() {
  if (typeof window === "undefined" || _bootstrapped) return;
  _bootstrapped = true;
  audioController.bootstrap({
    onFirstGesture: () => {
      void requestInboxNotificationPermission();
    },
  });
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

/**
 * Alert on a customer message. Sound plays even for the currently viewed
 * conversation; toast and desktop notification are skipped while it is visible.
 *
 * @param {{
 *   sessionId?: string,
 *   messageId?: string | number | null,
 *   message?: string,
 * }} opts
 */
export function alertInboxCustomerMessage({ sessionId, messageId, message } = {}) {
  const viewingThisChat =
    typeof document !== "undefined" &&
    !document.hidden &&
    sessionId != null &&
    String(getInboxSelectedConversationId() ?? "") === String(sessionId);
  const decision = inboxMessageSoundPolicy.decideCustomerMessage({
    soundEnabled: prefs.soundEnabled,
    isDuplicateMessage: false,
    viewingThisChat,
  });

  if (decision.playSound) playInboxMessageSound();

  if (!decision.showToast && !decision.showDesktop) return;

  const preview = String(message || "").trim();
  const body = preview.length > 140 ? `${preview.slice(0, 137)}…` : preview || "A customer sent a message.";
  if (decision.showToast && prefs.inAppEnabled !== false) {
    showInboxAppNotification({ title: "New customer message", body, sessionId, tone: "info" });
  }
  if (decision.showDesktop && prefs.desktopEnabled) {
    void showInboxMessageNotification({ title: "New customer message", body, sessionId });
  }
}

/**
 * Fire sound + in-app toast + desktop notification when a new handoff is detected.
 *
 * @param {{
 *   sessionId?: string,
 *   handoffId?: string | number | null,
 *   reasonType?: string | null,
 * }} opts
 */
export function alertInboxNewHandoff({ sessionId, handoffId, reasonType } = {}) {
  if (prefs.soundEnabled) playInboxMessageSound();

  const body = reasonType
    ? `Reason: ${reasonType}`
    : "A customer is waiting for an agent.";
  if (prefs.inAppEnabled !== false) {
    showInboxAppNotification({
      title: "New handoff — customer needs assistance",
      body,
      sessionId,
      tone: "warning",
    });
  }
  if (prefs.desktopEnabled) {
    void showInboxMessageNotification({
      title: "New handoff — customer needs assistance",
      body,
      sessionId,
    });
  }
}

const inboxAlertDispatcher = new InboxAlertDispatcher({
  onCustomerMessage: ({ sessionId, messageId, message }) =>
    alertInboxCustomerMessage({ sessionId, messageId, message }),
  onHandoff: ({ sessionId, handoffId, reasonType }) =>
    alertInboxNewHandoff({ sessionId, handoffId, reasonType }),
  fingerprint: createInboxAlertFingerprint,
  acceptEvent: (key, { fallback } = {}) =>
    shouldAlertOnce(key, Date.now(), fallback ? FALLBACK_DEDUPE_MS : undefined),
});

/** Tenant-wide socket entry point. */
export function dispatchInboxSocketMessage(data) {
  return inboxAlertDispatcher.dispatchSocketMessage(data);
}

/** Tenant-wide new-handoff socket entry point. */
export function dispatchInboxSocketHandoff(data) {
  return inboxAlertDispatcher.dispatchSocketHandoff(data);
}

/** Poll entry point. The caller keeps the original events for list updates. */
export function dispatchInboxPollEvents(payload) {
  return inboxAlertDispatcher.dispatchPoll(payload);
}
